import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import ProductsPickerItem from "./ProductsPickerItem";
import { formatAmount } from "../../api/DataTypeUtils";

export type ProductItemType = {
  id: string;
  name: string;
  productCode: string;
  unitPrice: number;
  quantity: number;
  tax: number;
  imageIds: string[];
};

export const defaultProductItem = {
  id: "",
  name: "",
  productCode: "",
  unitPrice: 0,
  quantity: 0,
  tax: 0,
  imageIds: [],
};

interface ProductsPickerProps {
  projectId: string;
  currency: string;
  renderTarget: (
    isOpen: boolean,
    setIsOpen: (open: boolean) => void,
    hasProducts: boolean
  ) => React.ReactNode;
  value: ProductItemType[];
  onChange: (value: ProductItemType[], totalAmount: any) => void;
  /** Direction the panel opens. Use "bottom start" when trigger is near left edge (e.g. chrome ext). Default: "bottom end" */
  panelAnchor?: "bottom end" | "bottom start" | "bottom";
}

const ProductsPicker = ({
  projectId,
  currency,
  renderTarget,
  value,
  onChange,
  panelAnchor = "bottom end",
}: ProductsPickerProps) => {
  const { t, i18n } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [productList, setProductList] = useState<ProductItemType[]>([defaultProductItem]);
  const totalAmount = productList
    .map((item) => item.unitPrice * item.quantity)
    .reduce((total, current) => total + current, 0);
  const hasProducts = productList.filter((item) => !!item.id).length > 0;

  const prevValueRef = useRef<string>("");
  useEffect(() => {
    const serialized = JSON.stringify(value);
    if (serialized === prevValueRef.current) return;
    prevValueRef.current = serialized;
    if (value) {
      setProductList(value.length === 0 ? [defaultProductItem] : value);
    }
  }, [value]);

  const validateProduct = () => {
    if (productList.length === 0) return false;
    for (let i = 0; i < productList.length; i++) {
      const product = productList[i];
      if (!product.id) return false;
    }
    return true;
  };

  const handleChange = (index: number, value: ProductItemType) => {
    const newProductList = productList.map((item, idx) => (idx === index ? value : item));
    setProductList([...newProductList]);
  };

  const handleAdd = () => {
    setProductList([...productList, defaultProductItem]);
  };

  const handleRemove = (index: number) => {
    const newProductList = productList.filter((_, idx) => idx !== index);
    setProductList([...newProductList]);
  };

  const handleCancel = () => {
    setProductList(value || [defaultProductItem]);
    setIsOpen(false);
  };

  const handleSave = () => {
    if (!validateProduct()) return;
    onChange(productList, totalAmount);
    setIsOpen(false);
  };

  const renderProductList = () => {
    return (
      <div>
        <div className="w-full flex items-center justify-between space-x-2 pb-2">
          <div className="flex items-center gap-2">
            <div className="w-60 text-sm text-gray-600">{t("Item")}</div>
            <div className="w-30 text-sm text-gray-600">{t("Price")}</div>
            <div className="w-20 text-sm text-gray-600">{t("Quantity")}</div>
            <div className="w-20 text-sm text-gray-600">{t("Tax %")}</div>
            <div className="w-30 text-sm text-gray-600">{t("Amount")}</div>
          </div>
          <div className="p-1 flex items-center rounded-md hover:bg-gray-100"></div>
        </div>
        {productList.map((item, index) => {
          return (
            <ProductsPickerItem
              key={`${item.id}_${index}`}
              idx={index}
              projectId={projectId}
              currency={currency}
              productItem={item}
              onChange={(productItem) => {
                handleChange(index, productItem);
              }}
              onRemove={(i) => {
                handleRemove(i);
              }}
            />
          );
        })}
      </div>
    );
  };

  const renderAction = () => {
    return (
      <div className="flex items-center justify-between gap-2">
        <div>
          {hasProducts ? (
            <div
              className="w-max px-2 py-1 text-xs text-gray-600 rounded-full border border-gray-300 hover:bg-gray-50 cursor-pointer"
              onClick={() => {
                onChange([], "");
                setIsOpen(false);
              }}
            >
              {t("Don't add products")}
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="px-2 py-1.5 text-xs rounded border border-gray-300 cursor-pointer"
            onClick={handleCancel}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            disabled={!validateProduct()}
            className="px-2 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-500 disabled:bg-gray-300 disabled:text-gray-600 cursor-pointer"
            onClick={handleSave}
          >
            {t("Save")}
          </button>
        </div>
      </div>
    );
  };

  return (
    <Popover>
      <PopoverButton as="div">
        {renderTarget(
          isOpen,
          (open) => {
            setIsOpen(open);
          },
          hasProducts
        )}
      </PopoverButton>
      {isOpen && (
        <PopoverPanel
          static
          anchor={{ to: panelAnchor, gap: 4, padding: 12 }}
          className={`p-2 bg-white rounded-md border border-gray-300 shadow-xl`}
        >
          <div>
            <div className="w-full mb-2 text-sm font-semibold">{t("Products")}</div>
            <div className="w-full max-h-60 overflow-y-auto">{renderProductList()}</div>
            <div className="w-full flex items-center justify-between">
              <div
                className="px-2 py-1.5 text-sm text-blue-600 flex items-center gap-2 cursor-pointer"
                onClick={handleAdd}
              >
                <Plus className="w-4 h-4 text-blue-600" />
                {t("Add product")}
              </div>
              <div className="pr-10 text-sm text-gray-700 font-semibold">
                <span className="pr-2">{t("Total")}</span>
                <span>{formatAmount(i18n.language, totalAmount, currency, "0")}</span>
              </div>
            </div>
            <div className="w-full my-2 border-b border-gray-200"></div>
            {renderAction()}
          </div>
        </PopoverPanel>
      )}
    </Popover>
  );
};

export default ProductsPicker;
